// src/pages/Expenses/parts/ExpenseFormModal.tsx
//
// Create / edit form for an expense.
//   create → POST /expenses      (multipart/form-data)
//   edit   → PUT  /expenses/:id  (multipart/form-data)
//
// Both handlers call ParseMultipartForm on the backend, so both requests go
// out as FormData — a JSON body is rejected before any field is read.
//
// Required on create: category, payee_name, amount, payment_mode,
// expense_date, description. transaction_ref becomes required the moment
// payment_mode isn't exactly "cash" (matches the backend's literal check).
// receipt_image is optional and accepts images or documents (e.g. PDFs).
//
// In edit mode the receipt preview starts as the stored CDN URL; a new file
// is only attached when the user actually picks one, otherwise the backend
// leaves the stored receipt untouched.

import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import {
  CATEGORY_SUGGESTIONS,
  ExpenseRecord,
  PAYMENT_MODE_OPTIONS,
  isReceiptImage,
  toDateParam,
} from './types';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import GlobalChipSelector from '../../../components/GlobalComponents/GlobalChipSelector';
import { GlobalDateField } from '../../../components/GlobalComponents/GlobalDateField';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';

interface ExpenseFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  /** Required in edit mode. */
  initialRecord?: ExpenseRecord | null;
  onClose: () => void;
  onSuccess: () => void;
}

const MAX_RECEIPT_BYTES = 8 * 1024 * 1024;

const emptyForm = {
  category: '',
  payeeName: '',
  payeeContact: '',
  amount: null as number | null,
  paymentMode: '',
  transactionRef: '',
  expenseDate: null as Date | null,
  description: '',
};

const ExpenseFormModal: React.FC<ExpenseFormModalProps> = ({
  open,
  mode,
  initialRecord,
  onClose,
  onSuccess,
}) => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrlRef = useRef<string | null>(null);

  const [form, setForm] = useState(emptyForm);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string>('');
  const [receiptIsImagePreview, setReceiptIsImagePreview] = useState(true);
  const [receiptDirty, setReceiptDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const isCreate = mode === 'create';
  const isCash = form.paymentMode === 'cash';

  const revokePreview = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) return;
    const r = initialRecord;
    revokePreview();
    setForm({
      category: r?.category ?? '',
      payeeName: r?.payeeName ?? '',
      payeeContact: r?.payeeContact ?? '',
      amount: r?.amount ?? null,
      paymentMode: r?.paymentMode ?? '',
      transactionRef: r?.transactionRef ?? '',
      expenseDate: r?.expenseDate ? new Date(`${r.expenseDate}T00:00:00`) : null,
      description: r?.description ?? '',
    });
    setReceiptFile(null);
    setReceiptPreview(r?.receiptUrl ?? '');
    setReceiptIsImagePreview(r?.receiptUrl ? isReceiptImage(r.receiptUrl) : true);
    setReceiptDirty(false);
    setSaving(false);
    setTouched(false);
    setIsClosing(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [open, initialRecord]);

  useEffect(() => revokePreview, []);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const setField = <K extends keyof typeof emptyForm>(key: K, value: (typeof emptyForm)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const missing = {
    category: !form.category.trim(),
    payeeName: !form.payeeName.trim(),
    amount: !form.amount || form.amount <= 0,
    paymentMode: !form.paymentMode,
    transactionRef: !isCash && !form.transactionRef.trim(),
    expenseDate: !form.expenseDate,
    description: !form.description.trim(),
  };
  const canSave = !Object.values(missing).some(Boolean);

  const handleClose = () => {
    if (saving) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';
    if (!isImage && !isPdf) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Unsupported file',
        detail: 'Upload an image or a PDF receipt.',
        life: 4000,
      });
      e.target.value = '';
      return;
    }
    if (file.size > MAX_RECEIPT_BYTES) {
      toast.current?.show({
        severity: 'warn',
        summary: 'File too large',
        detail: 'Choose a file under 8 MB.',
        life: 4000,
      });
      e.target.value = '';
      return;
    }

    revokePreview();
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setReceiptFile(file);
    setReceiptPreview(url);
    setReceiptIsImagePreview(isImage);
    setReceiptDirty(true);
  };

  const clearNewReceipt = () => {
    revokePreview();
    setReceiptFile(null);
    setReceiptDirty(false);
    setReceiptPreview(initialRecord?.receiptUrl ?? '');
    setReceiptIsImagePreview(
      initialRecord?.receiptUrl ? isReceiptImage(initialRecord.receiptUrl) : true,
    );
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const buildFormData = (): FormData => {
    const fd = new FormData();
    fd.append('category', form.category.trim());
    fd.append('payee_name', form.payeeName.trim());
    fd.append('payee_contact', form.payeeContact.trim());
    fd.append('amount', String(form.amount ?? ''));
    fd.append('payment_mode', form.paymentMode);
    fd.append('transaction_ref', form.transactionRef.trim());
    fd.append('expense_date', form.expenseDate ? toDateParam(form.expenseDate) : '');
    fd.append('description', form.description.trim());
    if (receiptDirty && receiptFile) fd.append('receipt_image', receiptFile);
    return fd;
  };

  const handleSave = async () => {
    setTouched(true);
    if (!canSave || saving) return;
    if (!isCreate && !initialRecord) return;

    setSaving(true);
    try {
      const fd = buildFormData();
      const res = isCreate
        ? await apiClient.post('api/expenses', fd)
        : await apiClient.put(`api/expenses/${initialRecord!.id}`, fd);

      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: isCreate ? 'Recorded' : 'Saved',
          detail: isCreate
            ? res.data.expense_status === 'approved'
              ? 'Expense recorded and approved.'
              : 'Expense recorded, awaiting approval.'
            : 'Expense updated.',
          life: 4000,
        });
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 300);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'The expense could not be saved.',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong while saving.',
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const showError = (key: keyof typeof missing) => touched && missing[key];

  const categoryChipOptions = CATEGORY_SUGGESTIONS.map((c) => ({
    value: c,
    label: c,
  }));

  const paymentModeChipOptions = PAYMENT_MODE_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
  }));

  const sectionCardStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.8)',
    borderRadius: 16,
    padding: 20,
    border: '1px solid rgba(59, 130, 246, 0.1)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
  };

  const SectionHeader: React.FC<{ icon: string; title: string; extra?: React.ReactNode }> = ({
    icon,
    title,
    extra,
  }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 16,
        paddingBottom: 12,
        borderBottom: '1px solid rgba(229, 231, 235, 0.6)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: 8,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          flexShrink: 0,
        }}
      >
        <i className={icon} style={{ fontSize: 15 }} />
      </div>
      <span
        style={{
          color: '#1e3a8a',
          fontWeight: 700,
          fontSize: 14,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          flex: 1,
        }}
      >
        {title}
      </span>
      {extra}
    </div>
  );

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
      <div
        style={{
          width: 54,
          height: 54,
          borderRadius: 14,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: 24,
          fontWeight: 700,
          boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
          flexShrink: 0,
          border: '3px solid white',
        }}
      >
        <i className={isCreate ? 'pi pi-plus' : 'pi pi-pencil'} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: '#1e40af',
            lineHeight: 1.2,
            marginBottom: 4,
          }}
        >
          {isCreate ? 'Record an expense' : 'Edit expense'}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {isCreate ? 'Add a new expense entry' : 'Update the expense details below'}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: 920, borderRadius: 24, overflow: 'hidden' }}
        contentStyle={{
          padding: 18,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
          borderRight: '12px solid rgba(255, 255, 255, 0.8)',
          borderTop: '12px solid rgba(255, 255, 255, 0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal
        dismissableMask={!saving}
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .p-dialog-mask {
            backdrop-filter: blur(4px);
            background-color: rgba(0, 0, 0, 0.25) !important;
            animation: maskFadeIn 0.35s ease;
          }
          .p-dialog-mask.mask-closing {
            animation: maskFadeOut 0.4s ease !important;
          }
          @keyframes maskFadeIn {
            from { opacity: 0; background-color: rgba(0, 0, 0, 0); }
            to { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
          }
          @keyframes maskFadeOut {
            from { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
            to { opacity: 0; background-color: rgba(0, 0, 0, 0); }
          }
          .p-dialog {
            animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          }
          .p-dialog.dialog-closing {
            animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          @keyframes dialogOpen {
            from { opacity: 0; transform: scale(0.92) translateY(-20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes dialogClose {
            from { opacity: 1; transform: scale(1) translateY(0); }
            to { opacity: 0; transform: scale(0.88) translateY(25px); }
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track {
            background: rgba(241, 245, 249, 0.5);
            border-radius: 10px;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb {
            background: rgba(148, 163, 184, 0.5);
            border-radius: 10px;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover {
            background: rgba(100, 116, 139, 0.7);
          }
          .expense-amount-field .p-inputnumber,
          .expense-amount-field .p-inputnumber-input {
            width: 100%;
          }
          .expense-amount-field .p-inputnumber-input {
            height: 44px;
            border-radius: 10px !important;
            border: 1px solid #e5e7eb !important;
            font-size: 14px !important;
            padding: 11px 14px !important;
            font-weight: 600;
            color: #1f2937;
            transition: all 0.2s ease;
          }
          .expense-amount-field .p-inputnumber-input:enabled:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
          }
          .expense-amount-field .p-inputnumber-input.p-invalid {
            border-color: #ef4444 !important;
          }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* ── Payee & Amount ── */}
          <div style={sectionCardStyle}>
            <SectionHeader icon="pi pi-user" title="Payee & Amount" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 16,
                }}
              >
                <GlobalInputField
                  label="Payee name"
                  required
                  value={form.payeeName}
                  onChange={(e) => setField('payeeName', e.target.value)}
                  placeholder="e.g. Sharma Electricals"
                  error={showError('payeeName') ? 'Payee name is required' : ''}
                  icon="pi pi-user"
                  disabled={saving}
                />
                <GlobalInputField
                  label="Payee contact"
                  value={form.payeeContact}
                  onChange={(e) => setField('payeeContact', e.target.value)}
                  placeholder="Phone or email"
                  icon="pi pi-phone"
                  disabled={saving}
                />
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 16,
                }}
              >
                <div className="expense-amount-field">
                  <label
                    style={{
                      display: 'block',
                      fontSize: 13,
                      fontWeight: 600,
                      color: showError('amount') ? '#ef4444' : '#1e3a8a',
                      marginBottom: 8,
                    }}
                  >
                    Amount <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <InputNumber
                    value={form.amount}
                    onValueChange={(e) => setField('amount', e.value ?? null)}
                    mode="currency"
                    currency="INR"
                    locale="en-IN"
                    min={0}
                    disabled={saving}
                    inputClassName={showError('amount') ? 'p-invalid' : ''}
                  />
                  {showError('amount') && (
                    <div
                      style={{
                        fontSize: 12,
                        color: '#ef4444',
                        fontWeight: 500,
                        marginTop: 6,
                      }}
                    >
                      Enter a valid amount
                    </div>
                  )}
                </div>

                <GlobalDateField
                  label="Expense date"
                  required
                  value={form.expenseDate}
                  onChange={(val) => setField('expenseDate', (val as Date) ?? null)}
                  placeholder="Select date"
                  dateFormat="dd M yy"
                  error={showError('expenseDate') ? 'Expense date is required' : ''}
                  disabled={saving}
                  fullWidth
                />
              </div>
            </div>
          </div>

          {/* ── Category ── */}
          <div style={sectionCardStyle}>
            <SectionHeader icon="pi pi-tags" title="Category" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <GlobalChipSelector
                label="Select a category"
                required
                options={categoryChipOptions}
                value={form.category}
                onChange={(val) => setField('category', val)}
                error={showError('category') ? 'Category is required' : ''}
                allowDeselect
              />

              <GlobalInputField
                label="Or type a custom category"
                value={form.category}
                onChange={(e) => setField('category', e.target.value)}
                placeholder="Custom category"
                error={showError('category') ? 'Category is required' : ''}
                icon="pi pi-pencil"
                disabled={saving}
              />
            </div>
          </div>

          {/* ── Payment ── */}
          <div style={sectionCardStyle}>
            <SectionHeader icon="pi pi-wallet" title="Payment Details" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <GlobalChipSelector
                label="Payment mode"
                required
                options={paymentModeChipOptions}
                value={form.paymentMode}
                onChange={(val) => {
                  setField('paymentMode', val);
                  if (val === 'cash') setField('transactionRef', '');
                }}
                error={showError('paymentMode') ? 'Payment mode is required' : ''}
              />

              {!isCash && (
                <div style={{ maxWidth: 400 }}>
                  <GlobalInputField
                    label="Transaction reference"
                    required
                    value={form.transactionRef}
                    onChange={(e) => setField('transactionRef', e.target.value)}
                    placeholder="Cheque no., UTR, transaction ID…"
                    error={
                      showError('transactionRef') ? 'Required for non-cash payments' : ''
                    }
                    icon="pi pi-hashtag"
                    disabled={saving}
                  />
                  {!showError('transactionRef') && (
                    <div
                      style={{
                        fontSize: 12,
                        color: '#94a3b8',
                        marginTop: 6,
                        fontWeight: 500,
                      }}
                    >
                      Required for any payment mode other than cash
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Description ── */}
          <div style={sectionCardStyle}>
            <SectionHeader icon="pi pi-align-left" title="Description" />
            <GlobalTextArea
              label="What was this expense for?"
              required
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder="Describe the expense…"
              rows={3}
              error={showError('description') ? 'Description is required' : ''}
              disabled={saving}
              fullWidth
            />
          </div>

          {/* ── Receipt ── */}
          <div style={sectionCardStyle}>
            <SectionHeader
              icon="pi pi-receipt"
              title="Receipt"
              extra={
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#94a3b8',
                    textTransform: 'none',
                    letterSpacing: 0,
                  }}
                >
                  Optional
                </span>
              }
            />

            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 14, fontWeight: 500 }}>
              Upload an image or PDF of the receipt / invoice · Max 8 MB.
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                flexWrap: 'wrap',
                padding: 16,
                borderRadius: 14,
                border: '1.5px dashed #c7d2fe',
                background: 'linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)',
              }}
            >
              {receiptPreview ? (
                receiptIsImagePreview ? (
                  <img
                    src={receiptPreview}
                    alt="Receipt"
                    style={{
                      width: 140,
                      height: 90,
                      borderRadius: 12,
                      objectFit: 'cover',
                      border: '2px solid rgba(59, 130, 246, 0.25)',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <a
                    href={receiptPreview}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      width: 140,
                      height: 90,
                      borderRadius: 12,
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#b91c1c',
                      textDecoration: 'none',
                      flexShrink: 0,
                    }}
                  >
                    <i className="pi pi-file-pdf" style={{ fontSize: 24 }} />
                    <span style={{ fontSize: 11, fontWeight: 700, marginTop: 4 }}>PDF</span>
                  </a>
                )
              ) : (
                <div
                  style={{
                    width: 140,
                    height: 90,
                    borderRadius: 12,
                    border: '1.5px dashed #c7d2fe',
                    background: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#64748b',
                    flexShrink: 0,
                  }}
                >
                  <i className="pi pi-image" style={{ fontSize: 22, color: '#3b82f6' }} />
                  <span style={{ fontSize: 11, fontWeight: 600, marginTop: 6 }}>No file</span>
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  flex: 1,
                  minWidth: 160,
                }}
              >
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 8,
                      border: '1.5px solid #3b82f6',
                      background: 'white',
                      color: '#3b82f6',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      opacity: saving ? 0.6 : 1,
                    }}
                  >
                    <i className="pi pi-upload" style={{ fontSize: 12 }} />
                    {receiptPreview ? 'Replace' : 'Upload'}
                  </button>

                  {receiptDirty && (
                    <button
                      type="button"
                      onClick={clearNewReceipt}
                      disabled={saving}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 8,
                        border: '1.5px solid #ef4444',
                        background: 'white',
                        color: '#ef4444',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        opacity: saving ? 0.6 : 1,
                      }}
                    >
                      <i className="pi pi-undo" style={{ fontSize: 12 }} />
                      Undo
                    </button>
                  )}
                </div>

                <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
                  {receiptDirty && receiptFile ? (
                    <span style={{ color: '#10b981', fontWeight: 600 }}>
                      <i className="pi pi-check-circle" style={{ marginRight: 4 }} />
                      {receiptFile.name} ({(receiptFile.size / 1024).toFixed(1)} KB) ready
                    </span>
                  ) : !receiptDirty && !isCreate && receiptPreview ? (
                    'Current receipt will stay unchanged'
                  ) : (
                    'Optional · image or PDF under 8 MB'
                  )}
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {touched && !canSave && (
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#b91c1c',
                background: 'rgba(239,68,68,.08)',
                border: '1px solid rgba(239,68,68,.2)',
                borderRadius: 12,
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <i className="pi pi-exclamation-circle" />
              Fill in the required fields before saving.
            </div>
          )}

          {/* Submit */}
          <div style={{ textAlign: 'center', marginTop: 4 }}>
            <GlobalButton
              label={
                saving
                  ? isCreate
                    ? 'Recording…'
                    : 'Saving…'
                  : isCreate
                    ? 'Record expense'
                    : 'Save changes'
              }
              onClick={handleSave}
              variant="primary"
              size="medium"
              disabled={saving}
              loading={saving}
              fullWidth={false}
              icon={isCreate ? 'pi pi-check' : 'pi pi-save'}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ExpenseFormModal;